import { useCallback, useContext, useEffect, useMemo, useReducer, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import type { DragEndEvent } from "@dnd-kit/core";

import { syncBrowseFilterPlatforms } from "../../utils/browseFilter";
import { type TalentBrowseProps, type TalentSelection } from "@/components/talentBrowse";
import { toast } from "@/components/ui/toast";
import { contentTags } from "../../shared/grailConstants";
import { BrowseFilter, ListAddCountsByMonth, Platform, SelectedTalent } from "../../shared/types";
import { getCurrencySymbol } from "../../shared/currency";
import { useExchangeRates } from "../../services/exchangeRates";
import { createListUpdate, deleteTalentFromList, exportListToGoogleSheet, incrementListAddHistory, updateList, useFreshCities, useFreshCountries, useFreshTalent, useListById, UserContext } from "../../services/firebase";
import { buildListSheetValues } from "../../utils/listExport";
import { orderedTalentIds } from "../../utils/orderedTalentIds";
import { calculateTalentWithPrices } from "../../utils/prices";
import {
    buildBrowseLists,
    buildListUpdates,
    findAddedTalentIds,
    findDeletedTalentIds,
    getInitialState,
    reduceListState,
    sumCustomTalentPrices,
    sumDefaultTalentPrices,
} from "./list";
import type { EditListBrowseProps } from "./components/EditListBrowse";
import type { ListSettingsFormProps } from "./components/ListSettingsForm";

/**
 * Queries, reducer, browse wiring, and save/export for Edit List.
 * Returns the props each column renders.
 */
export function useEditList() {
    const user = useContext(UserContext);
    const { listId } = useParams<{ listId: string }>();

    const talentQuery = useFreshTalent({ includeHidden: false, includeDeleted: false }); // don't allow managers to add hidden/deleted talent to lists, since they'll be seen by bookers
    const rawTalent = talentQuery.flatData;

    const countriesQuery = useFreshCountries();
    const countryList = countriesQuery.flatData.filter((country) => country.liveOnAirtable).map((country) => country.label).sort();
    const tagList = contentTags;
    const cityQuery = useFreshCities();
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort();

    const exchangeRates = useExchangeRates();
    const [state, dispatch] = useReducer(reduceListState, getInitialState());
    const {
        creatorRequest,
        creatorRequestLockFilters,
        filter,
        hidePublicFilters,
        hidePublicPrices,
        listUid,
        name,
        notesBookerViewable,
        notesRequired,
        originalList,
        selectedTalent,
        talentOrder,
        useCustomPrices,
        platforms,
    } = state;

    const isLoadingMore =
        (!rawTalent.length && (talentQuery.isFetching || talentQuery.isLoading));

    const list = useListById(listId || "");
    useEffect(() => {
        if (list?.id) {
            dispatch({ type: "setListFromDatabase", list });
        }
    }, [list]);

    const isListOwner = listUid === user?.id;
    const canReorder = !!(user?.roles?.admin || isListOwner);
    const hasCreatorRequestPermissions = user?.roles?.admin || (user?.roles?.manager && isListOwner);
    const areFiltersLocked = !!(creatorRequestLockFilters && !(user?.roles?.admin || user?.id === listUid));
    const isCurrencyLocked = !!(useCustomPrices || Object.keys(selectedTalent).length);

    const isCreatorRequestClosed = list && user && !isListOwner && !user?.roles?.admin && list?.creatorRequest === false;

    const browseLists = useMemo(
        () => (list ? buildBrowseLists(list, rawTalent, platforms) : []),
        [list, rawTalent, platforms],
    );

    const selected: TalentSelection = useMemo(() => {
        const record: TalentSelection = {};
        for (const [id, item] of Object.entries(selectedTalent)) {
            record[id] = { price: item.price || 0 };
        }
        return record;
    }, [selectedTalent]);

    const pricedForMini = useMemo(
        () => calculateTalentWithPrices(
            rawTalent,
            exchangeRates,
            { currency: filter.currency, campaignPlatform: platforms[0], campaignType: filter.campaignType || "Song" },
            selectedTalent,
        ),
        [rawTalent, exchangeRates, filter.currency, platforms, filter.campaignType, selectedTalent],
    );

    const handleFiltersChange = useCallback((value: BrowseFilter) => {
        dispatch({
            type: "updateFilter",
            filterPartial: {
                contentLanguage: value.contentLanguage,
                gender: value.gender,
                favoriteGenre: value.favoriteGenre,
                contentTags: value.contentTags,
                tagFilterLogic: value.tagFilterLogic,
                cities: value.cities,
                countries: value.countries,
                maleFollowerPctRange: value.maleFollowerPctRange,
                femaleFollowerPctRange: value.femaleFollowerPctRange,
                topFollowerCountries: value.topFollowerCountries,
                topFollowerAgeBrackets: value.topFollowerAgeBrackets,
                emailInBio: value.emailInBio,
                TikTok: value.TikTok,
                Instagram: value.Instagram,
                YouTube: value.YouTube,
            },
        });
    }, []);

    function handleSelectionChange(next: TalentSelection) {
        const merged: SelectedTalent = {};
        for (const id of Object.keys(next)) {
            const existing = selectedTalent[id];
            if (existing) {
                merged[id] = useCustomPrices
                    ? { ...existing, price: next[id].price }
                    : existing;
            } else {
                merged[id] = useCustomPrices
                    ? { price: next[id].price, notes: "", addedBy: user!.id! }
                    : { notes: "", addedBy: user!.id! };
            }
        }
        dispatch({
            type: "setStatePartial",
            payload: { selectedTalent: merged, talentOrder: orderedTalentIds(merged, talentOrder) },
        });
    }

    function handleTogglePlatform(platform: Platform, checked: boolean) {
        const nextPlatforms = checked
            ? [...platforms, platform]
            : platforms.filter((item) => item !== platform);
        dispatch({
            type: "setStatePartial",
            payload: {
                platforms: nextPlatforms,
                filter: syncBrowseFilterPlatforms(filter, nextPlatforms),
            },
        });
    }

    async function handleSave(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const removedIds = findDeletedTalentIds(state.originalList?.talent, selectedTalent);

        if (listId) {
            if (removedIds.length) {
                const deletedTalentPromises = [];

                for (const talId of removedIds) {
                    deletedTalentPromises.push(deleteTalentFromList(listId, talId));
                }

                await Promise.all(deletedTalentPromises);
            }

            const listUpdates = buildListUpdates({
                listId,
                creatorRequest: !!creatorRequest,
                creatorRequestLockFilters: !!creatorRequestLockFilters,
                name,
                platforms,
                useCustomPrices: !!useCustomPrices,
                filter,
                selectedTalent,
                talentOrder,
                notesBookerViewable: !!notesBookerViewable,
                notesRequired: !!notesRequired,
                hidePublicFilters: !!hidePublicFilters,
                hidePublicPrices: !!hidePublicPrices,
            });

            const res = await updateList(createListUpdate(listUpdates));

            if (res.success) {
                dispatch({ type: "setStatePartial", payload: { orderDirty: false } });
                toast.add({
                    type: "success",
                    title: "Changes saved",
                    description: "Your changes have been saved!",
                });
            } else {
                console.log(`Error saving list`, listUpdates);
                toast.add({
                    type: "error",
                    title: "Could not save",
                    description: "Your changes were not saved. Try again.",
                });
            }

            try {
                const netNewIds = findAddedTalentIds(originalList?.talent, selectedTalent);
                console.log(`TJN netNewIds`, netNewIds);

                if (netNewIds.length) {
                    const listAddKey: keyof ListAddCountsByMonth = creatorRequest ? "creatorRequestLists" : "managerLists";

                    incrementListAddHistory({ talentIds: netNewIds, managerId: user?.id || "", listAddKey });
                }
            } catch (e) {
                console.log(e);
            }


            console.log(`res`, res);
        }
    }

    function handleCustomPricesChange(isChecked: boolean) {
        dispatch({ type: "setStatePartial", payload: { useCustomPrices: isChecked } });
        dispatch({ type: "updateFilter", filterPartial: { lockCurrencyFromBooker: isChecked, lockCampaignType: isChecked } });
    }

    function handleCreatorRequestChange(isChecked: boolean) {
        dispatch({ type: "setStatePartial", payload: { creatorRequest: isChecked } });
    }

    function handleCreatorRequestLockFiltersChange(isChecked: boolean) {
        dispatch({ type: "setStatePartial", payload: { creatorRequestLockFilters: isChecked } });
    }

    const [isExportPending, setIsExportPending] = useState(false);
    const [sheetExportError, setSheetExportError] = useState<string | null>(null);

    const canExportList = (isListOwner || user?.roles?.admin) && !!name && !!listId;

    const handleExportToGoogleSheets = useCallback(async () => {
        if (!listId) {
            return;
        }
        setSheetExportError(null);
        setIsExportPending(true);
        const values = buildListSheetValues({
            platforms,
            talent: pricedForMini,
            selectedTalent,
            talentOrder,
        });
        try {
            const res = await exportListToGoogleSheet({
                listId,
                title: name,
                values,
                currencySymbol: getCurrencySymbol(filter.currency) || "$",
            });
            const payload = res.success && res.result && typeof res.result === "object"
                ? (res.result as { spreadsheetUrl?: string })
                : undefined;
            const sheetUrl = payload?.spreadsheetUrl;
            if (sheetUrl) {
                toast.add({
                    type: "success",
                    title: "Spreadsheet created",
                    description: "Your Google Sheet is ready.",
                    actionProps: {
                        children: "Open",
                        onClick: () => {
                            window.open(sheetUrl, "_blank", "noopener,noreferrer");
                        },
                    },
                });
            } else if (!res.success) {
                const err = res.error as { message?: string } | undefined;
                setSheetExportError(err?.message || "Could not create the spreadsheet.");
            } else {
                setSheetExportError("Could not create the spreadsheet.");
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "Export failed.";
            setSheetExportError(msg);
        } finally {
            setIsExportPending(false);
        }
    }, [listId, name, filter.currency, platforms, pricedForMini, selectedTalent, talentOrder]);

    function handleTalentDragEnd(event: DragEndEvent) {
        if (!canReorder) {
            return;
        }
        const { active, over } = event;
        if (over && active.id !== over.id) {
            dispatch({
                type: "reorderTalent",
                activeId: String(active.id),
                overId: String(over.id),
            });
        }
    }

    const talentTotal = useCustomPrices
        ? sumCustomTalentPrices(selectedTalent)
        : filter.campaignType === "Song"
            ? sumDefaultTalentPrices(Object.keys(selectedTalent).map((talId) => pricedForMini.find((tal) => tal.id === talId)))
            : undefined;

    const browse: TalentBrowseProps | undefined = name ? {
        talent: rawTalent,
        lists: browseLists,
        platforms,
        campaignType: filter.campaignType || "Song",
        currency: filter.currency,
        exchangeRates,
        selected,
        onSelectionChange: handleSelectionChange,
        defaultListId: list?.id,
        defaultFilters: filter,
        onFiltersChange: handleFiltersChange,
        filterSheet: {
            showPriceChips: true,
            currency: filter.currency,
            countryList,
            tagList,
            cityList,
            disabled: areFiltersLocked,
        },
        empty: rawTalent.length ? "No creators match your filters." : "No creators to show.",
    } : undefined;

    const browseColumn: EditListBrowseProps = {
        isReady: !!name,
        isLoading: isLoadingMore,
        currency: {
            value: filter.currency,
            disabled: isCurrencyLocked,
            locked: !!filter.lockCurrencyFromBooker || !!filter.lockCurrencyFromList,
            onChange: (value) => dispatch({ type: "updateFilter", filterPartial: { currency: (value || "USD") as BrowseFilter["currency"] } }),
        },
        platforms: {
            value: platforms,
            required: true,
            onToggle: handleTogglePlatform,
        },
        campaignType: filter.showCampaignTypeSelect ? {
            value: filter.campaignType,
            disabled: !!filter.lockCampaignType || isCurrencyLocked,
            onChange: (next) => dispatch({ type: "updateFilter", filterPartial: { campaignType: next } }),
        } : undefined,
        browse,
    };

    const settings: ListSettingsFormProps = {
        onSubmit: handleSave,
        sheetExportError,
        onDismissExportError: () => setSheetExportError(null),
        isCreatorRequestClosed: Boolean(isCreatorRequestClosed),
        canExportList: Boolean(canExportList),
        isExportPending,
        onExport: () => void handleExportToGoogleSheets(),
        name,
        isNameDisabled: !isListOwner && !user?.roles?.admin,
        onNameChange: (value) => dispatch({ type: "setStatePartial", payload: { name: value } }),
        useCustomPrices: !!useCustomPrices,
        isCustomPricesDisabled: !!Object.keys(selectedTalent).length || user?.id !== listUid,
        onCustomPricesChange: handleCustomPricesChange,
        hasCreatorRequestPermissions: !!hasCreatorRequestPermissions,
        creatorRequest: !!creatorRequest,
        onCreatorRequestChange: handleCreatorRequestChange,
        creatorRequestLockFilters: !!creatorRequestLockFilters,
        onCreatorRequestLockFiltersChange: handleCreatorRequestLockFiltersChange,
        canEditOwnerSettings: !!(isListOwner || user?.roles?.admin),
        notesBookerViewable: !!notesBookerViewable,
        onNotesBookerViewableChange: (checked) => dispatch({ type: "setStatePartial", payload: { notesBookerViewable: checked } }),
        notesRequired: !!notesRequired,
        onNotesRequiredChange: (checked) => dispatch({ type: "setStatePartial", payload: { notesRequired: checked } }),
        hidePublicFilters: !!hidePublicFilters,
        onHidePublicFiltersChange: (checked) => dispatch({ type: "setStatePartial", payload: { hidePublicFilters: checked } }),
        hidePublicPrices: !!hidePublicPrices,
        onHidePublicPricesChange: (checked) => dispatch({ type: "setStatePartial", payload: { hidePublicPrices: checked } }),
        currencySymbol: getCurrencySymbol(filter.currency),
        talentTotal,
        cart: {
            talentOrder,
            talent: pricedForMini,
            platforms,
            filter,
            selectedTalent,
            notesBookerViewable,
            notesRequired,
            useCustomPrices,
            listUid,
            user,
            canReorder,
            onDragEnd: handleTalentDragEnd,
            onNotesChange: (id, notes) => dispatch({ type: "updateTalentNotes", id, notes }),
            onRemove: (id) => dispatch({ type: "removeTalent", id }),
        },
    };

    return { browseColumn, settings };
}
